import PosButton from '@/components/PosButton';
import { APIEndpoint } from '@/root/app';
import { IS_POS } from '@/utils/env';
import { playSound } from '@/utils/sound';
import { STORAGE_POS_LOGIN_HISTORY } from '@/utils/storage';
import { getAdminAvatar } from '@/utils/uiHelper';
import { LockOutlined, RollbackOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Form, Input } from 'antd';
import QueryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useRequest } from '../../api/hooks';
import styles from './style.less';

interface LoginInfo {
  userName: string;
  avatar: string;
}

type Props = {
  mode?: 'page' | 'modal';
  onSuccess?: (loginInfo: LoginInfo) => void;
};

const Login: React.FC<Props> = ({ mode = 'page', onSuccess }: Props) => {
  const isModal = mode === 'modal';
  const isPOS = IS_POS;
  const [form] = Form.useForm();
  const [loginHistory, setLoginHistory] = useLocalStorage<LoginInfo[]>(STORAGE_POS_LOGIN_HISTORY, []);
  const [accountMode, setAccountMode] = useState<'select' | 'input'>(isPOS && loginHistory.length ? 'select' : 'input');
  const [selectedAccount, setSelectedAccount] = useState<LoginInfo>(null);

  const {
    loaded,
    loading,
    error,
    send: login,
    payload: loginInfo,
  } = useRequest<LoginInfo>(
    {
      method: 'post',
      url: `/auth/login`,
    },
    { manual: true }
  );

  const queryString = QueryString.parse(decodeURIComponent(location.search));
  const returnUrl = queryString.returnUrl as string;

  // 企微中配置的 clientCode
  const clientCode = queryString.clientCode;

  // 服务器要求进行账密登录后绑定账号的 token
  const bindToken = queryString.bindToken;

  const onFinish = values => {
    values.clientCode = clientCode;
    values.bindToken = bindToken;
    login({ params: { clientCode }, data: values }).then(loginInfo => {
      // 记录登录历史账户
      if (isPOS) {
        const history = loginHistory.slice(0, 3);
        const findIndex = history.findIndex(item => item.userName === loginInfo.userName);
        if (findIndex > -1) {
          history.splice(findIndex, 1);
        }
        history.unshift({ userName: loginInfo.userName, avatar: loginInfo.avatar });
        setLoginHistory(history);
      }

      if (loginInfo && onSuccess) {
        onSuccess(loginInfo);
      }
    });
  };

  useEffect(() => {
    // 如果有 clientCode, 重定向到后端
    if (clientCode && !bindToken) {
      window.location.href = `${APIEndpoint}/auth/login/thirdParty?clientCode=${clientCode}&returnUrl=${returnUrl}`;
    }
  }, [clientCode, bindToken]);

  useEffect(() => {
    if (loaded) {
      if (!isModal) {
        location.href = returnUrl || '/';
      }
    }
  }, [loaded, loginInfo, error, isModal]);

  return (
    <div className={`${styles.container} ${isPOS ? styles.pos : ''} ${isModal ? styles.modal : ''}`}>
      {!isModal && (
        <div className={styles.brand}>
          <div className={styles.logo}>
            <img
              draggable={false}
              // src={`https://cdn.sjianke.com/assets/images/logo${isPOS ? '' : '-t'}.svg`}
              src={require('../../assets/logo.svg')}
              height={150}
              alt={'veeyung'}
            />
          </div>
        </div>
      )}
      {clientCode && !bindToken ? (
        <div>自动登录中, 请稍等</div>
      ) : (
        <div className={styles.main}>
          <Form className={styles.loginForm} form={form} initialValues={{ remember: true }} onFinish={onFinish}>
            {accountMode === 'select' && !selectedAccount ? (
              <>
                <div className={styles.historyList}>
                  <h4>点击选择最近登录帐号</h4>
                  {loginHistory.map(item => (
                    <div
                      key={item.userName}
                      className={`${styles.selectItem} ${styles.selectable}`}
                      onClick={() => {
                        playSound('click');
                        setSelectedAccount(item);
                        form.setFieldsValue({ username: item.userName });
                      }}>
                      {getAdminAvatar(item.avatar, '', { size: 28 })}
                      {item.userName}
                    </div>
                  ))}
                </div>
                <PosButton
                  size={'large'}
                  type={'primary'}
                  style={{ width: '100%' }}
                  onClick={() => {
                    setAccountMode('input');
                  }}>
                  登录其它账号
                </PosButton>
              </>
            ) : null}

            {accountMode === 'select' && selectedAccount && (
              <Form.Item name="username">
                <div className={styles.selectItemWrapper}>
                  <div className={styles.selectItem}>
                    {getAdminAvatar(selectedAccount.avatar, '', { size: 28 })}
                    {selectedAccount.userName}
                  </div>
                  <PosButton
                    onClick={() => {
                      setSelectedAccount(null);
                      form.setFieldsValue({ username: '' });
                    }}
                    size={'large'}
                    shape={'circle'}
                    ghost
                    icon={<RollbackOutlined />}
                  />
                </div>
              </Form.Item>
            )}
            {accountMode === 'input' && (
              <Form.Item name="username" rules={[{ required: true, message: '请输入用户名或手机号' }]}>
                <Input prefix={<UserOutlined className="site-form-item-icon" />} placeholder="请输入用户名或手机号" />
              </Form.Item>
            )}

            {(accountMode === 'input' || !!selectedAccount) && (
              <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
                <Input
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  type="password"
                  placeholder="请输入密码"
                />
              </Form.Item>
            )}

            {bindToken && (
              <Form.Item>
                <Alert showIcon message={<>首次使用请通过账户密码登录</>} description={<>登录后自动完成绑定</>} />
              </Form.Item>
            )}
            {(accountMode === 'input' || !!selectedAccount) && (
              <Form.Item>
                <PosButton
                  type="primary"
                  size={'large'}
                  loading={loading}
                  htmlType="submit"
                  className={styles.loginFormButton}>
                  登录
                </PosButton>
              </Form.Item>
            )}
          </Form>
        </div>
      )}
    </div>
  );
};
export default Login;
